import { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faPlus } from '@/fontawesome';
import CreateMissingSymbolsModal from '@/components/admin/recommendations/CreateMissingSymbolsModal.tsx';
import type { SymbolEntry } from '@/components/admin/recommendations/CreateMissingSymbolsModal.tsx';
import { handleAPIError } from '@/utils/toasts';
import { formatRFCDate } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import type { JournalIBUnknownSymbolStat } from '@/models';

interface JournalUnknownSymbolsPanelProps {
  onSymbolsCreated: () => void;
}

/** Underlyings that journal imports found trades for but could not resolve to a symbol. Creating
 *  the symbol is enough - the executions are already stored and the next import derives the
 *  positions from them. */
export default function JournalUnknownSymbolsPanel({ onSymbolsCreated }: JournalUnknownSymbolsPanelProps) {
  const [unknownSymbols, setUnknownSymbols] = useState<JournalIBUnknownSymbolStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await APIRoutes.listAllJournalIBUnknownSymbols();
      setUnknownSymbols(response.unknownSymbols);
    } catch (err) {
      handleAPIError(err, 'Fehlende Journal-Symbole konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSymbolsReady = () => {
    setIsModalOpen(false);
    void load();
    onSymbolsCreated();
  };

  // A fresh array identity would re-seed the open modal and discard what the admin has typed.
  const entries: SymbolEntry[] = useMemo(() => unknownSymbols.map((unknown) => ({
    rawInput: unknown.ticker,
    parsedTicker: unknown.ticker,
    parsedExchange: null,
    existingSymbol: null,
  })), [unknownSymbols]);

  if (loading) {
    return null;
  }

  if (unknownSymbols.length === 0) {
    return null;
  }

  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-amber-300 dark:border-amber-700 p-6 mb-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faTriangleExclamation} className="w-5 h-5 text-amber-500 mr-3" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Aus Trading-Journal-Importen fehlend ({unknownSymbols.length})
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Für diese Basiswerte wurden Trades importiert, aber es gibt kein Symbol. Die Ausführungen sind
                gespeichert — sobald das Symbol existiert, legt der nächste Import die Trades rückwirkend an.
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="shrink-0 px-4 py-2 bg-amber-600 text-white rounded-lg transition-colors flex items-center hover:bg-amber-700 cursor-pointer"
          >
            <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-2" />
            Symbole anlegen
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ticker</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ausführungen</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nutzer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Zeitraum</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {unknownSymbols.map((unknown) => (
                <tr key={unknown.ticker}>
                  <td className="px-6 py-3 whitespace-nowrap font-medium text-gray-900 dark:text-white">{unknown.ticker}</td>
                  <td className="px-6 py-3 whitespace-nowrap text-right text-gray-700 dark:text-gray-300">{unknown.executionCount}</td>
                  <td className="px-6 py-3 whitespace-nowrap text-right text-gray-700 dark:text-gray-300">{unknown.userCount}</td>
                  <td className="px-6 py-3 whitespace-nowrap text-gray-500 dark:text-gray-400">
                    {formatRFCDate(unknown.firstTradeDate)} – {formatRFCDate(unknown.lastTradeDate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <CreateMissingSymbolsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        symbols={entries}
        onSymbolsReady={handleSymbolsReady}
      />
    </>
  );
}
